/**
 * Port of app_router.dart:72-90 — the "already logged in, go home" half of the
 * redirect callback. Wraps the landing page and the auth branch.
 *
 * Flutter's version, restated:
 *
 *   - `/auth/register` while authenticated (L72-78): SUPERAGENT and AGENT stay
 *     (they use the register form to create users for their properties);
 *     everyone else is bounced to their role home.
 *   - any other `/auth/*` route, or the landing page, while authenticated
 *     (L84-90): bounced to the role home.
 *
 * `allowManagerRoles` selects the first behaviour; the register route is the
 * only place that passes it.
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { homePathForRole } from '../paths';

export interface RedirectIfAuthenticatedProps {
  /**
   * When true, an authenticated AGENT/SUPERAGENT is allowed to stay on this
   * route instead of being redirected home (app_router.dart:74-77).
   */
  allowManagerRoles?: boolean;
}

export function RedirectIfAuthenticated({
  allowManagerRoles = false,
}: RedirectIfAuthenticatedProps): React.JSX.Element {
  const { user } = useAuth();

  if (user === null) {
    return <Outlet />;
  }

  if (allowManagerRoles && (user.role === 'SUPERAGENT' || user.role === 'AGENT')) {
    return <Outlet />;
  }

  return <Navigate to={homePathForRole(user.role)} replace />;
}

export default RedirectIfAuthenticated;
